import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import {
  generateBackupExcel,
  formatDateForExcel,
  formatDateOnly,
  formatCurrencyForExcel,
  formatNumberForExcel,
  type BackupSheet,
} from "@/lib/backup-export";
import { toast } from "sonner";

export type BackupCategory =
  | "orders"
  | "cash"
  | "expenses"
  | "products"
  | "customers"
  | "logs";

interface UseBackupExportReturn {
  isExporting: boolean;
  progress: number;
  progressMessage: string;
  estimatedRecords: number | null;
  exportBackup: (
    categories: BackupCategory[],
    startDate: Date,
    endDate: Date
  ) => Promise<void>;
  estimateRecords: (
    categories: BackupCategory[],
    startDate: Date,
    endDate: Date
  ) => Promise<void>;
}

export function useBackupExport(): UseBackupExportReturn {
  const { activeRestaurant } = useRestaurantContext();
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [estimatedRecords, setEstimatedRecords] = useState<number | null>(null);

  const estimateRecords = useCallback(
    async (categories: BackupCategory[], startDate: Date, endDate: Date) => {
      if (!activeRestaurant?.id) return;

      const restaurantId = activeRestaurant.id;
      const startISO = startDate.toISOString();
      const endISO = endDate.toISOString();
      let total = 0;

      try {
        if (categories.includes("orders")) {
          const { count: ordersCount } = await supabase
            .from("orders")
            .select("*", { count: "exact", head: true })
            .eq("restaurant_id", restaurantId)
            .gte("created_at", startISO)
            .lte("created_at", endISO);
          total += ordersCount || 0;
        }

        if (categories.includes("cash")) {
          const { count: cashCount } = await supabase
            .from("cash_registers")
            .select("*", { count: "exact", head: true })
            .eq("restaurant_id", restaurantId)
            .gte("opened_at", startISO)
            .lte("opened_at", endISO);
          total += cashCount || 0;
        }

        if (categories.includes("expenses")) {
          const { count: expensesCount } = await supabase
            .from("expenses")
            .select("*", { count: "exact", head: true })
            .eq("restaurant_id", restaurantId)
            .gte("expense_date", startDate.toISOString().split("T")[0])
            .lte("expense_date", endDate.toISOString().split("T")[0]);
          total += expensesCount || 0;
        }

        if (categories.includes("products")) {
          const { count: productsCount } = await supabase
            .from("products")
            .select("*", { count: "exact", head: true })
            .eq("restaurant_id", restaurantId);
          total += productsCount || 0;
        }

        if (categories.includes("customers")) {
          const { count: customersCount } = await supabase
            .from("customers")
            .select("*", { count: "exact", head: true })
            .eq("restaurant_id", restaurantId);
          total += customersCount || 0;
        }

        setEstimatedRecords(total);
      } catch (error) {
        console.error("Error estimating records:", error);
      }
    },
    [activeRestaurant?.id]
  );

  const exportBackup = useCallback(
    async (categories: BackupCategory[], startDate: Date, endDate: Date) => {
      if (!activeRestaurant?.id) {
        toast.error("Restaurante não selecionado");
        return;
      }

      setIsExporting(true);
      setProgress(0);

      const restaurantId = activeRestaurant.id;
      const startISO = startDate.toISOString();
      const endISO = endDate.toISOString();
      const sheets: BackupSheet[] = [];

      try {
        const totalSteps = categories.length;
        let currentStep = 0;

        // ORDERS
        if (categories.includes("orders")) {
          setProgressMessage("Exportando pedidos...");
          
          const { data: orders } = await supabase
            .from("orders")
            .select(`
              id,
              order_number,
              order_type,
              status,
              subtotal,
              discount,
              additional_fees,
              total,
              customer_name,
              notes,
              created_at,
              updated_at,
              payment_method:payment_methods(name)
            `)
            .eq("restaurant_id", restaurantId)
            .gte("created_at", startISO)
            .lte("created_at", endISO)
            .order("created_at", { ascending: false })
            .limit(50000);

          sheets.push({
            name: "Pedidos",
            data: (orders || []).map((o) => ({
              "Nº Pedido": o.order_number,
              "Data/Hora": formatDateForExcel(o.created_at),
              "Tipo": o.order_type === "table" ? "Mesa" : o.order_type === "tab" ? "Comanda" : "Balcão",
              "Status": o.status === "delivered" ? "Finalizado" : o.status === "cancelled" ? "Cancelado" : o.status === "pending" ? "Pendente" : o.status === "preparing" ? "Preparando" : "Pronto",
              "Cliente": o.customer_name || "-",
              "Subtotal": formatCurrencyForExcel(o.subtotal),
              "Desconto": formatCurrencyForExcel(o.discount),
              "Taxas Adicionais": formatCurrencyForExcel(o.additional_fees),
              "Total": formatCurrencyForExcel(o.total),
              "Forma Pagamento": (o.payment_method as { name: string } | null)?.name || "-",
              "Observações": o.notes || "",
              "Atualizado em": formatDateForExcel(o.updated_at),
            })),
          });

          // Get order items
          const orderIds = (orders || []).map((o) => o.id);
          if (orderIds.length > 0) {
            const { data: orderItems } = await supabase
              .from("order_items")
              .select(`
                quantity,
                unit_price,
                total_price,
                notes,
                product_name,
                order:orders(order_number)
              `)
              .in("order_id", orderIds)
              .limit(50000);

            sheets.push({
              name: "Itens Vendidos",
              data: (orderItems || []).map((item) => ({
                "Nº Pedido": (item.order as { order_number: number } | null)?.order_number || "",
                "Produto": item.product_name || "-",
                "Quantidade": formatNumberForExcel(item.quantity),
                "Preço Unitário": formatCurrencyForExcel(item.unit_price),
                "Total": formatCurrencyForExcel(item.total_price),
                "Observações": item.notes || "",
              })),
            });
          }

          currentStep++;
          setProgress((currentStep / totalSteps) * 100);
        }

        // CASH REGISTERS
        if (categories.includes("cash")) {
          setProgressMessage("Exportando movimentações de caixa...");

          const { data: cashRegisters } = await supabase
            .from("cash_registers")
            .select("id, opened_at, closed_at, opening_amount, closing_amount, expected_amount, difference, status, notes")
            .eq("restaurant_id", restaurantId)
            .gte("opened_at", startISO)
            .lte("opened_at", endISO)
            .order("opened_at", { ascending: false })
            .limit(5000);

          sheets.push({
            name: "Caixas",
            data: (cashRegisters || []).map((c) => ({
              "Abertura": formatDateForExcel(c.opened_at),
              "Fechamento": formatDateForExcel(c.closed_at),
              "Valor Inicial": formatCurrencyForExcel(c.opening_amount),
              "Valor Final": formatCurrencyForExcel(c.closing_amount),
              "Esperado": formatCurrencyForExcel(c.expected_amount),
              "Diferença": formatCurrencyForExcel(c.difference),
              "Status": c.status === "open" ? "Aberto" : "Fechado",
              "Observações": c.notes || "",
            })),
          });

          // Get cash movements
          const cashIds = (cashRegisters || []).map((c) => c.id);
          if (cashIds.length > 0) {
            const { data: movements } = await supabase
              .from("cash_movements")
              .select("created_at, type, amount, reason")
              .in("cash_register_id", cashIds)
              .order("created_at", { ascending: false })
              .limit(50000);

            sheets.push({
              name: "Mov. Caixa",
              data: (movements || []).map((m) => ({
                "Data/Hora": formatDateForExcel(m.created_at),
                "Tipo": m.type === "withdrawal" ? "Sangria" : 
                       m.type === "supply" ? "Suprimento" : m.type,
                "Valor": formatCurrencyForExcel(m.amount),
                "Motivo": m.reason || "",
              })),
            });
          }

          currentStep++;
          setProgress((currentStep / totalSteps) * 100);
        }

        // EXPENSES
        if (categories.includes("expenses")) {
          setProgressMessage("Exportando despesas...");

          const { data: expenses } = await supabase
            .from("expenses")
            .select(`
              title,
              expense_date,
              due_date,
              amount,
              status,
              paid_at,
              description,
              category:expense_categories(name),
              supplier:suppliers(name)
            `)
            .eq("restaurant_id", restaurantId)
            .gte("expense_date", startDate.toISOString().split("T")[0])
            .lte("expense_date", endDate.toISOString().split("T")[0])
            .order("expense_date", { ascending: false })
            .limit(50000);

          sheets.push({
            name: "Despesas",
            data: (expenses || []).map((e) => ({
              "Título": e.title,
              "Data": formatDateOnly(e.expense_date),
              "Vencimento": formatDateOnly(e.due_date),
              "Valor": formatCurrencyForExcel(e.amount),
              "Status": e.status === "paid" ? "Pago" : e.status === "cancelled" ? "Cancelado" : "Em aberto",
              "Pago em": formatDateOnly(e.paid_at),
              "Categoria": (e.category as { name: string } | null)?.name || "-",
              "Fornecedor": (e.supplier as { name: string } | null)?.name || "-",
              "Descrição": e.description || "",
            })),
          });

          currentStep++;
          setProgress((currentStep / totalSteps) * 100);
        }

        // PRODUCTS & INGREDIENTS
        if (categories.includes("products")) {
          setProgressMessage("Exportando produtos...");

          const { data: products } = await supabase
            .from("products")
            .select(`
              name,
              price,
              cost_per_unit,
              is_active,
              barcode,
              category:categories(name)
            `)
            .eq("restaurant_id", restaurantId)
            .order("name")
            .limit(50000);

          sheets.push({
            name: "Produtos",
            data: (products || []).map((p) => ({
              "Nome": p.name,
              "Preço": formatCurrencyForExcel(p.price),
              "Custo": formatCurrencyForExcel(p.cost_per_unit),
              "Margem": p.price && p.cost_per_unit 
                ? `${(((p.price - p.cost_per_unit) / p.price) * 100).toFixed(1)}%` 
                : "-",
              "Categoria": (p.category as { name: string } | null)?.name || "-",
              "Código de Barras": p.barcode || "-",
              "Ativo": p.is_active ? "Sim" : "Não",
            })),
          });

          const { data: ingredients } = await supabase
            .from("ingredients")
            .select("name, unit, current_stock, minimum_stock, cost_per_unit, is_active")
            .eq("restaurant_id", restaurantId)
            .order("name")
            .limit(50000);

          sheets.push({
            name: "Ingredientes",
            data: (ingredients || []).map((i) => ({
              "Nome": i.name,
              "Unidade": i.unit,
              "Estoque Atual": formatNumberForExcel(i.current_stock),
              "Estoque Mínimo": formatNumberForExcel(i.minimum_stock),
              "Custo por Unidade": formatCurrencyForExcel(i.cost_per_unit),
              "Ativo": i.is_active ? "Sim" : "Não",
            })),
          });

          // Stock movements
          const { data: stockMovements } = await supabase
            .from("stock_movements")
            .select(`
              created_at,
              type,
              quantity,
              unit_cost,
              reason,
              ingredient:ingredients(name)
            `)
            .eq("restaurant_id", restaurantId)
            .gte("created_at", startISO)
            .lte("created_at", endISO)
            .order("created_at", { ascending: false })
            .limit(50000);

          sheets.push({
            name: "Mov. Estoque",
            data: (stockMovements || []).map((s) => ({
              "Data/Hora": formatDateForExcel(s.created_at),
              "Ingrediente": (s.ingredient as { name: string } | null)?.name || "-",
              "Tipo": s.type === "entry" ? "Entrada" : 
                     s.type === "exit" ? "Saída" : 
                     s.type === "adjustment" ? "Ajuste" : s.type,
              "Quantidade": formatNumberForExcel(s.quantity),
              "Custo Unitário": formatCurrencyForExcel(s.unit_cost),
              "Motivo": s.reason || "",
            })),
          });

          currentStep++;
          setProgress((currentStep / totalSteps) * 100);
        }

        // CUSTOMERS
        if (categories.includes("customers")) {
          setProgressMessage("Exportando clientes...");

          const { data: customers } = await supabase
            .from("customers")
            .select("name, phone, email, address, total_orders, total_spent, last_order_at, created_at")
            .eq("restaurant_id", restaurantId)
            .order("name")
            .limit(50000);

          sheets.push({
            name: "Clientes",
            data: (customers || []).map((c) => ({
              "Nome": c.name,
              "Telefone": c.phone || "-",
              "Email": c.email || "-",
              "Endereço": c.address || "-",
              "Total Pedidos": formatNumberForExcel(c.total_orders),
              "Total Gasto": formatCurrencyForExcel(c.total_spent),
              "Último Pedido": formatDateOnly(c.last_order_at),
              "Cadastrado em": formatDateOnly(c.created_at),
            })),
          });

          const { data: ratings } = await supabase
            .from("customer_ratings")
            .select("created_at, table_number, overall_experience, food_quality, service_quality, environment, comments, customer_name")
            .eq("restaurant_id", restaurantId)
            .gte("created_at", startISO)
            .lte("created_at", endISO)
            .order("created_at", { ascending: false })
            .limit(50000);

          sheets.push({
            name: "Avaliações",
            data: (ratings || []).map((r) => ({
              "Data": formatDateForExcel(r.created_at),
              "Mesa": r.table_number || "-",
              "Cliente": r.customer_name || "-",
              "Experiência Geral": r.overall_experience || "-",
              "Qualidade Comida": r.food_quality || "-",
              "Atendimento": r.service_quality || "-",
              "Ambiente": r.environment || "-",
              "Comentários": r.comments || "",
            })),
          });

          currentStep++;
          setProgress((currentStep / totalSteps) * 100);
        }

        // LOGS
        if (categories.includes("logs")) {
          setProgressMessage("Exportando logs...");

          const { data: logs } = await supabase
            .from("operation_logs")
            .select("created_at, entity_type, action, details")
            .eq("restaurant_id", restaurantId)
            .gte("created_at", startISO)
            .lte("created_at", endISO)
            .order("created_at", { ascending: false })
            .limit(50000);

          sheets.push({
            name: "Logs",
            data: (logs || []).map((l) => ({
              "Data/Hora": formatDateForExcel(l.created_at),
              "Entidade": l.entity_type,
              "Ação": l.action,
              "Detalhes": l.details ? JSON.stringify(l.details) : "",
            })),
          });

          currentStep++;
          setProgress((currentStep / totalSteps) * 100);
        }

        // Generate Excel file
        setProgressMessage("Gerando arquivo...");
        generateBackupExcel(sheets, {
          restaurantName: activeRestaurant.name,
          startDate,
          endDate,
        });

        toast.success("Backup exportado com sucesso!");
      } catch (error) {
        console.error("Error exporting backup:", error);
        toast.error("Erro ao exportar backup");
      } finally {
        setIsExporting(false);
        setProgress(0);
        setProgressMessage("");
      }
    },
    [activeRestaurant]
  );

  return {
    isExporting,
    progress,
    progressMessage,
    estimatedRecords,
    exportBackup,
    estimateRecords,
  };
}
